import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import {
  CheckCircle2, XCircle, ArrowLeft, Activity, Eye,
  MousePointerClick, AlertTriangle, Clock,
} from "lucide-react";

export default function StudentReport() {
  const { lessonId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { if (!loading && !user) navigate("/", { replace: true }); }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get(`/progress/${lessonId}/report`)
      .then(({ data }) => setReport(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load report"));
  }, [user, lessonId]);

  if (!user || !report) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center text-slate-500">
        {err ? err : "Loading debrief…"}
      </div>
    );
  }

  const ev = report.events_by_type || {};
  const clicks = ev.click || 0;
  const skipAttempts = ev.skip_attempt || 0;
  const focusLost = ev.focus_lost || 0;
  const audioPauses = ev.audio_pause || 0;
  const completed = !!report.progress?.completed_at;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="report-page">
      <Link to="/dashboard" className="text-slate-500 hover:text-[var(--brand-primary)] inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold" data-testid="report-back">
        <ArrowLeft className="w-3 h-3" /> Back to dashboard
      </Link>

      <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Student debrief</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">{report.lesson?.title}</h1>
          <div className="mt-2 text-sm text-slate-600">{report.user?.name} · <span className="font-mono text-slate-500">{report.user?.email}</span></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm px-6 py-4 text-right shadow-sm">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Final score</div>
          <div className="font-heading text-4xl font-bold text-[var(--brand-primary)] tabular-nums mt-1" data-testid="report-score">
            {report.progress?.score ?? "—"}%
          </div>
          <div className="mt-1">
            <span className={`status-badge ${completed ? "status-success" : "status-warning"}`}>
              {completed ? "Completed" : "In progress"}
            </span>
          </div>
        </div>
      </div>

      {/* TELEMETRY */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile icon={<MousePointerClick className="w-4 h-4" />} label="Clicks"         value={clicks} />
        <Tile icon={<AlertTriangle className="w-4 h-4" />}     label="Skip attempts"  value={skipAttempts}  alert={skipAttempts > 0} />
        <Tile icon={<Eye className="w-4 h-4" />}               label="Focus lost"     value={focusLost}     alert={focusLost > 0} />
        <Tile icon={<Activity className="w-4 h-4" />}          label="Audio pauses"   value={audioPauses} />
        <Tile icon={<Clock className="w-4 h-4" />}             label="Events total"   value={report.total_events} />
      </div>

      {/* ANSWERS */}
      <div className="mt-10">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Quiz record ({report.answers.length})</div>
        <div className="mt-3 space-y-3">
          {report.answers.length === 0 && <div className="text-slate-500 text-sm">No quiz answers recorded.</div>}
          {report.answers.map((a, i) => (
            <div key={i} className={`bg-white rounded-sm p-5 border ${a.correct ? "border-emerald-200" : "border-red-200"}`} data-testid={`report-answer-${i}`}>
              <div className="flex items-start gap-3">
                {a.correct
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  : <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <div className="font-heading font-semibold text-slate-900">{a.question}</div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2">
                    {a.options.map((opt, oi) => {
                      const wasPicked = oi === a.selected_index;
                      const isCorrect = oi === a.correct_index;
                      return (
                        <div key={oi} className={`text-sm font-mono px-3 py-2 border rounded-sm flex items-center gap-2 ${
                          isCorrect ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                          : wasPicked ? "border-red-300 bg-red-50 text-red-900"
                          : "border-slate-200 text-slate-600 bg-slate-50"}`}>
                          <span className="font-bold">{String.fromCharCode(65 + oi)}.</span> {opt}
                          {wasPicked && !isCorrect && <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-red-700">Your pick</span>}
                          {wasPicked && isCorrect && <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Your pick ✓</span>}
                          {!wasPicked && isCorrect && <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Correct</span>}
                        </div>
                      );
                    })}
                  </div>
                  {a.explanation && (
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-sm">
                      <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[var(--brand-primary)]">Explanation</span>
                      <br />{a.explanation}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* INTEGRITY */}
      {(skipAttempts > 0 || focusLost > 0) && (
        <div className="mt-10 bg-red-50 border border-red-200 rounded-sm p-6" data-testid="integrity-alert">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-red-700">Instructor attention</div>
          <h3 className="font-heading text-xl font-bold text-red-900 mt-1">Engagement flags</h3>
          <ul className="mt-3 space-y-1 text-red-900 text-sm">
            {skipAttempts > 0 && <li>• {skipAttempts} attempt(s) to skip narration before completion.</li>}
            {focusLost > 0 && <li>• Tab/window lost focus {focusLost} time(s) during the brief.</li>}
          </ul>
        </div>
      )}

      <div className="mt-10 flex gap-3">
        <Link to={`/lesson/${lessonId}`}>
          <Button variant="outline" className="border-slate-300 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] rounded-sm font-medium" data-testid="report-retake">
            Re-take the brief
          </Button>
        </Link>
        <Link to="/dashboard">
          <Button className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-medium" data-testid="report-dashboard">
            Back to dashboard
          </Button>
        </Link>
      </div>
    </div>
  );
}

function Tile({ icon, label, value, alert }) {
  return (
    <div
      className={`bg-white border rounded-sm p-4 ${alert ? "border-red-300" : "border-slate-200"}`}
      data-testid={`tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold ${alert ? "text-red-700" : "text-slate-500"}`}>
        {icon} {label}
      </div>
      <div className={`font-heading text-2xl font-bold tabular-nums mt-2 ${alert ? "text-red-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
